import { Link } from 'react-router';
import { Message } from '@gonfalon/messages/Message';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import cx from 'clsx';

import { InstructionListChangeKind } from '#components/InstructionList/instructionListUtils';
import type { ConflictsInfoForInstruction } from '#utils/instructions/shared/types';
import { trackScheduledConflictsEvent } from '#utils/scheduledChanges/trackScheduledConflictsEvent';

import { InstructionListItemIcon } from './InstructionListItemIcon';

import styles from './styles.module.css';

type InstructionListItemProps = {
  className?: string;
  description?: string | React.ReactNode;
  showConflictLink?: boolean;
  conflictLink?: string;
  changeKind?: InstructionListChangeKind;
  children?: React.ReactNode;
  onExit?: () => void;
  conflict?: ConflictsInfoForInstruction;
};

export function InstructionListItem({
  className,
  description,
  showConflictLink = true,
  conflictLink,
  changeKind = InstructionListChangeKind.DEFAULT,
  children,
  conflict,
  onExit,
}: InstructionListItemProps) {
  const testId = changeKind ? `instruction-list-item-${changeKind}` : 'instruction-list-item';

  return (
    <li
      className={cx(styles.InstructionItem, styles.InstructionListItem, className, {
        [styles.decimal]: changeKind === InstructionListChangeKind.DECIMAL,
        [styles.default]: changeKind === InstructionListChangeKind.DEFAULT,
      })}
      data-test-id={testId}
    >
      <span className="u-flex">
        <InstructionListItemIcon className={styles.instructionListItemIconNotNested} changeKind={changeKind} />
        <span className={styles.instructionItemContent}>
          {!!description && <span>{description} </span>}
          {children}
          {showConflictLink && conflictLink && (
            <Link
              to={conflictLink}
              onClick={() => {
                trackScheduledConflictsEvent('View Conflict Clicked');
                onExit?.();
              }}
            >
              <Message id="pendingChanges.changeList.viewConflict" />
            </Link>
          )}
          {conflict?.conflicts[0]?.conflictReason && (
            <div className={styles.inlineConflict}>
              <TooltipTrigger delay={0}>
                <Focusable>
                  <span tabIndex={0} role="button">
                    <Message id="pendingChanges.changeList.viewConflict" />
                  </span>
                </Focusable>
                <Tooltip>{conflict.conflicts[0]?.conflictReason}</Tooltip>
              </TooltipTrigger>
            </div>
          )}
        </span>
      </span>
    </li>
  );
}
